import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useLoad } from '../hooks';
import { PageHead } from '../Layout';
import { Disclosure } from '../components/Disclosure';
import { Tabs, type TabOption } from '../components/Tabs';
import { formatHour } from '../format';
import { MODEL_SELECTOR_IDS, tweetSlotHours } from '../../shared/types';
import type {
  ArticleFormat,
  CostSummary,
  GoldExample,
  ModelCatalogOption,
  ModelCatalogResponse,
  ModelSelectorRole,
  SettingsShape,
  XCostSummary,
} from '../../shared/types';

type ModelFieldKey = ModelSelectorRole;

interface ModelField {
  key: ModelFieldKey;
  label: string;
  hint?: string;
}

const MODEL_GROUPS: Array<{ label: string; hint: string; fields: ModelField[] }> = [
  {
    label: 'Writing and conversation',
    hint: 'Models that reason, select, and respond.',
    fields: [
      { key: 'curator', label: 'Curator' },
      { key: 'editor', label: 'Editor' },
    ],
  },
  {
    label: 'Media processing',
    hint: 'Models that interpret or generate Library media.',
    fields: [
      { key: 'vision', label: 'Vision' },
      { key: 'transcription', label: 'Transcription' },
      { key: 'youtubeTranscription', label: 'YouTube transcription', hint: 'Requires public YouTube URL support.' },
      { key: 'previewGeneration', label: 'Preview artwork', hint: 'Used only when no source image is available.' },
    ],
  },
  {
    label: 'Search',
    hint: 'Models behind Library retrieval.',
    fields: [
      { key: 'embeddings', label: 'Embeddings', hint: 'Changing this requires re-embedding the Library.' },
      { key: 'reranker', label: 'Reranker', hint: 'Orders the 25 closest vector matches.' },
    ],
  },
];

const ACTIVE_MODEL_GROUPS = MODEL_GROUPS.filter((group) => group.label !== 'Writing and conversation');
const MODEL_FIELD_COUNT = ACTIVE_MODEL_GROUPS.reduce((count, group) => count + group.fields.length, 0);

const CUSTOM_VALUE = '__custom__';

type SettingsTab = 'general' | 'articles' | 'tweets' | 'models' | 'costs';
const SETTINGS_TAB_OPTIONS: readonly TabOption<SettingsTab>[] = [
  { value: 'general', label: 'General' },
  { value: 'models', label: 'Models' },
  { value: 'costs', label: 'Costs' },
];

type CostTab = 'openrouter' | 'x';
const COST_TAB_OPTIONS: readonly TabOption<CostTab>[] = [
  { value: 'openrouter', label: 'OpenRouter' },
  { value: 'x', label: 'X' },
];

function formatCostUsd(value: number): string {
  if (value === 0) return '$0.00';
  if (value < 0.01) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
}

/**
 * The only cadence shape the simple editor can round-trip: minute 0, a fixed
 * hour, and an N-day interval. The server treats this shape as a rolling
 * interval from the last article run; other hand-edited cron expressions keep
 * ordinary calendar-cron semantics and stay in the advanced editor.
 */
const SIMPLE_CADENCE_RE = /^0\s+(\d{1,2})\s+\*\/(\d{1,2})\s+\*\s+\*$/;

function parseCadence(cron: string): { hour: number; days: number } | null {
  const m = SIMPLE_CADENCE_RE.exec(cron.trim());
  if (!m) return null;
  const hour = Number(m[1]);
  const days = Number(m[2]);
  if (hour < 0 || hour > 23 || days < 1) return null;
  return { hour, days };
}

function buildCadence(hour: number, days: number): string {
  return `0 ${hour} */${days} * *`;
}

function jitterHour(hour: number, offset: number): number {
  return (hour + offset + 24) % 24;
}

const LINK_STYLE: CSSProperties = {
  border: 'none',
  background: 'none',
  padding: 0,
  font: 'inherit',
  fontSize: 12,
  color: 'var(--muted)',
  textDecoration: 'underline',
  cursor: 'pointer',
};

function GoldExampleEditor({ format }: { format: ArticleFormat }) {
  const [items, setItems] = useState<GoldExample[]>([]);
  const [drafts, setDrafts] = useState<Record<number, Pick<GoldExample, 'title' | 'body' | 'enabled'>>>({});
  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');
  const [busy, setBusy] = useState<number | 'new' | null>(null);

  const { error, setError } = useLoad(
    useCallback(async () => {
      const res = await api.get<{ items: GoldExample[] }>(`/gold?format=${format}`);
      setItems(res.items);
      setDrafts(
        Object.fromEntries(
          res.items.map((item) => [
            item.id,
            { title: item.title, body: item.body, enabled: item.enabled },
          ]),
        ),
      );
    }, [format]),
  );

  function patch(id: number, value: Partial<Pick<GoldExample, 'title' | 'body' | 'enabled'>>) {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...value } }));
  }

  async function save(id: number) {
    const draft = drafts[id];
    if (!draft || !draft.body.trim() || (format === 'article' && !draft.title?.trim())) return;
    setBusy(id);
    try {
      const saved = await api.patch<GoldExample>(`/gold/${id}`, {
        title: format === 'article' ? draft.title?.trim() || null : null,
        body: draft.body.trim(),
        enabled: draft.enabled,
      });
      setItems((current) => current.map((item) => (item.id === id ? saved : item)));
      setDrafts((current) => ({
        ...current,
        [id]: { title: saved.title, body: saved.body, enabled: saved.enabled },
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function remove(id: number) {
    if (!window.confirm('Delete this gold example?')) return;
    setBusy(id);
    try {
      await api.delete(`/gold/${id}`);
      setItems((current) => current.filter((item) => item.id !== id));
      setDrafts((current) => {
        const next = { ...current };
        delete next[id];
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function create() {
    if (!newBody.trim() || (format === 'article' && !newTitle.trim())) return;
    setBusy('new');
    try {
      const created = await api.post<GoldExample>('/gold', {
        format,
        title: format === 'article' ? newTitle.trim() : null,
        body: newBody.trim(),
        enabled: true,
      });
      setNewTitle('');
      setNewBody('');
      setItems((current) => [created, ...current]);
      setDrafts((current) => ({
        ...current,
        [created.id]: { title: created.title, body: created.body, enabled: created.enabled },
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="gold-examples" aria-labelledby={`${format}-gold-title`}>
      <div className="label" id={`${format}-gold-title`} style={{ marginBottom: 8 }}>
        Examples
      </div>
      <p className="hint" style={{ marginTop: 0 }}>
        Examples used only for {format} output.
        {format === 'article' ? ' Titles also guide future title generation.' : ''}
      </p>
      {error ? <p className="fail">{error}</p> : null}
      {items.map((item) => {
        const draft = drafts[item.id] ?? item;
        return (
          <div key={item.id} style={{ borderTop: '1px solid var(--line)', padding: '12px 0' }}>
            {format === 'article' ? (
              <div className="field" style={{ marginBottom: 8 }}>
                <label className="hint" htmlFor={`gold-title-${item.id}`}>Title</label>
                <input
                  id={`gold-title-${item.id}`}
                  value={draft.title ?? ''}
                  disabled={busy === item.id}
                  onChange={(e) => patch(item.id, { title: e.target.value })}
                />
              </div>
            ) : null}
            <textarea
              aria-label={`${format} gold example ${item.id}`}
              rows={Math.max(2, Math.ceil(draft.body.length / 100))}
              value={draft.body}
              disabled={busy === item.id}
              onChange={(e) => patch(item.id, { body: e.target.value })}
            />
            <div className="row" style={{ alignItems: 'end', gap: 10, marginTop: 8 }}>
              <label className="hint" style={{ paddingBottom: 8 }}>
                <input
                  type="checkbox"
                  style={{ width: 'auto', marginRight: 6 }}
                  checked={draft.enabled}
                  disabled={busy === item.id}
                  onChange={(e) => patch(item.id, { enabled: e.target.checked })}
                />
                Use
              </label>
              <button
                type="button"
                disabled={busy === item.id || !draft.body.trim() || (format === 'article' && !draft.title?.trim())}
                onClick={() => void save(item.id)}
              >
                {busy === item.id ? 'Saving…' : 'Save'}
              </button>
              <button type="button" disabled={busy === item.id} onClick={() => void remove(item.id)}>
                Delete
              </button>
            </div>
          </div>
        );
      })}
      <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12 }}>
        {format === 'article' ? (
          <div className="field" style={{ marginBottom: 8 }}>
            <label className="hint" htmlFor="new-article-gold-title">Title</label>
            <input
              id="new-article-gold-title"
              value={newTitle}
              disabled={busy === 'new'}
              placeholder="Add the article title…"
              onChange={(e) => setNewTitle(e.target.value)}
            />
          </div>
        ) : null}
        <textarea
          aria-label={`New ${format} gold example`}
          rows={2}
          value={newBody}
          disabled={busy === 'new'}
          placeholder={`Add a ${format} example…`}
          onChange={(e) => setNewBody(e.target.value)}
        />
        <button
          type="button"
          style={{ marginTop: 8 }}
          disabled={busy === 'new' || !newBody.trim() || (format === 'article' && !newTitle.trim())}
          onClick={() => void create()}
        >
          {busy === 'new' ? 'Adding…' : 'Add example'}
        </button>
      </div>
    </section>
  );
}

/** Load examples only when requested; keep drafts mounted after the first expansion. */
function GoldExamples({ format }: { format: ArticleFormat }) {
  const [opened, setOpened] = useState(false);
  return (
    <Disclosure
      title="Anchor examples"
      meta="Optional reference material"
      className="examples-disclosure"
      onToggle={(event) => {
        if (event.currentTarget.open) setOpened(true);
      }}
    >
      {opened && <GoldExampleEditor format={format} />}
    </Disclosure>
  );
}

export default function Settings() {
  const [s, setS] = useState<SettingsShape | null>(null);
  const savedSettings = useRef<SettingsShape | null>(null);
  const [modelCatalog, setModelCatalog] = useState<ModelCatalogResponse | null>(null);
  const [costs, setCosts] = useState<CostSummary | null>(null);
  const [xCosts, setXCosts] = useState<XCostSummary | null>(null);
  const [modelCatalogError, setModelCatalogError] = useState<string | null>(null);
  const [costsError, setCostsError] = useState<string | null>(null);
  const [xCostsError, setXCostsError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cadenceAdvanced, setCadenceAdvanced] = useState(false);
  const [activeSettingsTab, setActiveSettingsTab] = useState<SettingsTab>('general');
  const [activeCostTab, setActiveCostTab] = useState<CostTab>('openrouter');
  // Explicit "Custom…" picks per model-select field; undefined defers to
  // whether the stored value matches a known candidate id.
  const [customModelFields, setCustomModelFields] = useState<Record<string, boolean>>({});

  useEffect(() => {
    void (async () => {
      try {
        const loaded = await api.get<SettingsShape>('/settings');
        setS(loaded);
        savedSettings.current = loaded;
        setCadenceAdvanced(parseCadence(loaded.articleCadenceCron) === null);
        const [openRouterCosts, xApiCosts, catalog] = await Promise.allSettled([
          api.get<CostSummary>('/settings/costs'),
          api.get<XCostSummary>('/settings/x-costs'),
          api.get<ModelCatalogResponse>('/settings/model-catalog'),
        ]);
        if (openRouterCosts.status === 'fulfilled') setCosts(openRouterCosts.value);
        else setCostsError('OpenRouter costs could not be loaded.');
        if (xApiCosts.status === 'fulfilled') setXCosts(xApiCosts.value);
        else setXCostsError('X costs could not be loaded.');
        if (catalog.status === 'fulfilled') setModelCatalog(catalog.value);
        else setModelCatalogError('The live model catalog could not be loaded. Saved model IDs are still available.');
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, []);

  function patch<K extends keyof SettingsShape>(key: K, value: SettingsShape[K]) {
    setS((prev) => (prev ? { ...prev, [key]: value } : prev));
    setStatus(null);
  }

  async function save() {
    if (!s) return;
    setBusy(true);
    setError(null);
    try {
      const baseline = savedSettings.current;
      const payload: Record<string, unknown> = {};
      const scalarKeys = [
        'brandContext', 'styleGuide', 'tweetStyleGuide', 'articleCadenceCron', 'tweetSlots',
        'mode', 'maxWords', 'strictTitle', 'announceInGroup',
      ] as const;
      for (const key of scalarKeys) {
        if (!baseline || s[key] !== baseline[key]) payload[key] = s[key];
      }
      const modelPatch = Object.fromEntries(
        Object.entries(s.models).filter(([key, value]) => !baseline || baseline.models[key] !== value),
      );
      if (Object.keys(modelPatch).length) payload.models = modelPatch;
      const agentPatch: Record<string, Record<string, unknown>> = {};
      for (const slug of new Set([
        ...Object.keys(baseline?.agentSettings ?? {}),
        ...Object.keys(s.agentSettings),
      ])) {
        const before = baseline?.agentSettings[slug] ?? {};
        const after = s.agentSettings[slug] ?? {};
        const changed: Record<string, unknown> = {};
        for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
          const next = (after as Record<string, unknown>)[key];
          if (JSON.stringify(next) !== JSON.stringify((before as Record<string, unknown>)[key])) {
            changed[key] = next ?? null;
          }
        }
        if (Object.keys(changed).length) agentPatch[slug] = changed;
      }
      if (Object.keys(agentPatch).length) payload.agentSettings = agentPatch;

      const saved = await api.put<SettingsShape>('/settings', payload);
      setS(saved);
      savedSettings.current = saved;
      setStatus('Saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (error && !s) return <p className="fail">{error}</p>;
  if (!s) return <div className="empty">Loading…</div>;
  // Narrowed alias: the `!s` guard above doesn't propagate into nested
  // functions like renderModelField, so they read from `settings` instead.
  const settings = s;
  const editorialGames = (s.agentSettings.editorialGames ?? {}) as Record<string, unknown>;
  const gameQuietHours = Array.isArray(editorialGames.quietHours)
    ? editorialGames.quietHours[0] as { start?: unknown; end?: unknown } | undefined
    : undefined;

  function patchEditorialGames(key: string, value: unknown) {
    patch('agentSettings', {
      ...settings.agentSettings,
      editorialGames: { ...editorialGames, [key]: value },
    });
  }

  function patchGameQuietHour(key: 'start' | 'end', value: string) {
    patchEditorialGames('quietHours', [{
      start: typeof gameQuietHours?.start === 'string' ? gameQuietHours.start : '22:00',
      end: typeof gameQuietHours?.end === 'string' ? gameQuietHours.end : '09:00',
      [key]: value,
    }]);
  }

  const cadence = parseCadence(s.articleCadenceCron) ?? { hour: 16, days: 3 };
  const slotHours = tweetSlotHours(s.tweetSlots, s.agentSettings.editor?.tweetSlotHours);
  const slotCaption =
    slotHours.length === 0 ? 'No tweets scheduled' : slotHours.map((h) => formatHour(h)).join(', ');
  const largestXCost = Math.max(
    ...(xCosts?.breakdown.map((item) => item.costUsd) ?? [0]),
    0.000001,
  );

  function optionsForModelField(key: ModelFieldKey): ModelCatalogOption[] {
    const byId = new Map((modelCatalog?.options ?? []).map((option) => [option.id, option]));
    const curated = MODEL_SELECTOR_IDS[key]
      .map((id) => byId.get(id))
      .filter((option): option is ModelCatalogOption => Boolean(option));
    return key === 'vision'
      ? curated.filter((option) => option.inputModalities.includes('image'))
      : curated;
  }

  /** Shared select+custom-escape-hatch field for a Models row (curated candidates or priced options). */
  function renderModelField(m: ModelField) {
    const options = optionsForModelField(m.key);
    const value = settings.models[m.key] ?? '';
    const isKnownOption = options.some((option) => option.id === value);
    const isCustom = customModelFields[m.key] ?? !isKnownOption;
    return (
      <div className="model-field" key={m.key}>
        <div className="model-field-name">
          <label className="label" htmlFor={`model-${m.key}`}>
            {m.label}
          </label>
          {m.hint ? <span className="hint">{m.hint}</span> : null}
        </div>
        <div className="model-field-control">
          <select
            id={`model-${m.key}`}
            value={isCustom ? CUSTOM_VALUE : value}
            onChange={(e) => {
              const next = e.target.value;
              if (next === CUSTOM_VALUE) {
                setCustomModelFields((prev) => ({ ...prev, [m.key]: true }));
                return;
              }
              setCustomModelFields((prev) => ({ ...prev, [m.key]: false }));
              patch('models', { ...settings.models, [m.key]: next });
            }}
          >
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label} · {option.priceLabel}
              </option>
            ))}
            <option value={CUSTOM_VALUE}>Custom…</option>
          </select>
          {isCustom ? (
            <input
              className="mono"
              value={value}
              placeholder="openrouter/model-id"
              aria-label={`${m.label} custom model ID`}
              onChange={(e) => patch('models', { ...settings.models, [m.key]: e.target.value })}
            />
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHead
        title="Settings"
        subtitle="Workspace-wide controls, model assignments, and recorded provider costs."
        actions={
          <button className="primary" onClick={() => void save()} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        }
      />

      {error ? <p className="fail">{error}</p> : null}
      {status ? <p className="muted">{status}</p> : null}

      <Tabs
        ariaLabel="Settings sections"
        idPrefix="settings"
        options={SETTINGS_TAB_OPTIONS}
        value={activeSettingsTab}
        onChange={setActiveSettingsTab}
        className="settings-primary-tabs"
      />

      <div className="settings-workspace">
        <section
          className="settings-tab-panel"
          id="settings-panel-general"
          role="tabpanel"
          aria-labelledby="settings-tab-general"
          hidden={activeSettingsTab !== 'general'}
        >
          <div className="settings-panel-head">
            <div>
              <h2>General</h2>
              <p>Shared safeguards for group participation.</p>
            </div>
          </div>
          <p className="editorial-alert">
            Publication voices, cadence, research, and delivery now live in <Link to="/dashboard">Publications</Link>.
          </p>
          <fieldset className="settings-fieldset settings-form">
            <legend>Idea games</legend>
            <p className="hint">Optional prompts that invite members to help develop promising material. They never gate publication.</p>
            <label className="settings-checkbox settings-checkbox-row">
              <input
                type="checkbox"
                checked={editorialGames.enabled === true}
                onChange={(event) => patchEditorialGames('enabled', event.target.checked)}
              />
              <span>Enable idea games</span>
            </label>
            <div className="settings-general-row">
              <label className="field settings-control-small">
                <span className="label">Daily cap</span>
                <input
                  type="number"
                  min={1}
                  max={24}
                  value={typeof editorialGames.maxPromptsPerDay === 'number' ? editorialGames.maxPromptsPerDay : 1}
                  onChange={(event) => patchEditorialGames('maxPromptsPerDay', Number(event.target.value))}
                />
              </label>
              <label className="field settings-control-small">
                <span className="label">Cooldown minutes</span>
                <input
                  type="number"
                  min={1}
                  max={10080}
                  value={typeof editorialGames.cooldownMinutes === 'number' ? editorialGames.cooldownMinutes : 360}
                  onChange={(event) => patchEditorialGames('cooldownMinutes', Number(event.target.value))}
                />
              </label>
            </div>
            <div className="settings-general-row">
              <label className="field settings-control-small">
                <span className="label">Quiet hours begin</span>
                <input type="time" value={typeof gameQuietHours?.start === 'string' ? gameQuietHours.start : '22:00'} onChange={(event) => patchGameQuietHour('start', event.target.value)} />
              </label>
              <label className="field settings-control-small">
                <span className="label">Quiet hours end</span>
                <input type="time" value={typeof gameQuietHours?.end === 'string' ? gameQuietHours.end : '09:00'} onChange={(event) => patchGameQuietHour('end', event.target.value)} />
              </label>
            </div>
          </fieldset>
          <fieldset className="settings-fieldset settings-form" hidden aria-hidden="true">
            <div className="field">
              <label className="label" htmlFor="brand">Brand context</label>
              <span className="hint">Who the collective is and what it is doing. Injected into every prompt.</span>
              <textarea id="brand" rows={8} value={s.brandContext} onChange={(e) => patch('brandContext', e.target.value)} />
            </div>

            <div className="settings-general-row">
              <div className="field settings-control-medium">
                <label className="label" htmlFor="mode">Publishing mode</label>
                <select id="mode" value={s.mode} onChange={(e) => patch('mode', e.target.value as SettingsShape['mode'])}>
                  <option value="auto">Auto — publish eligible output</option>
                  <option value="review">Review — hold output for approval</option>
                </select>
                <span className="hint">Applies to articles and tweets.</span>
              </div>
              <label className="settings-checkbox">
                <input
                  type="checkbox"
                  checked={s.announceInGroup}
                  onChange={(e) => patch('announceInGroup', e.target.checked)}
                />
                <span>Announce publications in Telegram</span>
              </label>
            </div>

          </fieldset>
        </section>

        <section
          className="settings-tab-panel"
          id="settings-panel-articles"
          role="tabpanel"
          aria-labelledby="settings-tab-articles"
          hidden={activeSettingsTab !== 'articles'}
        >
          <div className="settings-panel-head">
            <div>
              <h2>Articles</h2>
              <p>Retired legacy controls.</p>
            </div>
          </div>
          <p className="editorial-alert">Article profiles are managed in <Link to="/dashboard">Publications</Link>.</p>
          <fieldset className="settings-fieldset settings-form" hidden aria-hidden="true">
            <div className="field">
              <label className="label" htmlFor="style">Article style guide</label>
              <span className="hint">Used when the Editor assembles an article from an idea package.</span>
              <textarea id="style" rows={10} value={s.styleGuide} onChange={(e) => patch('styleGuide', e.target.value)} />
            </div>

            <div className="settings-article-grid">
              <div className="field">
                <label className="label" htmlFor="cadence">Article cadence</label>
                {cadenceAdvanced ? (
                  <>
                    <input id="cadence" className="mono" value={s.articleCadenceCron} onChange={(e) => patch('articleCadenceCron', e.target.value)} />
                    <span className="hint">
                      Advanced calendar schedule. The simple controls use a rolling interval from the last article run.
                      {parseCadence(s.articleCadenceCron) ? <> <button type="button" style={LINK_STYLE} onClick={() => setCadenceAdvanced(false)}>Use simple controls</button></> : null}
                    </span>
                  </>
                ) : (
                  <>
                    <div className="cadence-controls">
                      <span>Every</span>
                      <input id="cadence" type="number" min={1} max={99} value={cadence.days} onChange={(e) => {
                        const days = Math.max(1, Math.floor(Number(e.target.value)) || 1);
                        patch('articleCadenceCron', buildCadence(cadence.hour, days));
                      }} />
                      <span>day{cadence.days === 1 ? '' : 's'} at</span>
                      <input type="time" step={3600} value={`${String(cadence.hour).padStart(2, '0')}:00`} onChange={(e) => {
                        const hour = Number(e.target.value.split(':')[0]);
                        if (Number.isFinite(hour)) patch('articleCadenceCron', buildCadence(hour, cadence.days));
                      }} />
                      <button type="button" style={LINK_STYLE} onClick={() => setCadenceAdvanced(true)}>Advanced</button>
                    </div>
                    <span className="hint">
                      Publishes between {formatHour(jitterHour(cadence.hour, -2))} and {formatHour(jitterHour(cadence.hour, 2))}, counting local calendar days from the last article run.
                    </span>
                  </>
                )}
              </div>
              <div className="field settings-control-small">
                <label className="label" htmlFor="maxwords">Max words</label>
                <input id="maxwords" type="number" min={1} value={s.maxWords} onChange={(e) => patch('maxWords', Number(e.target.value))} />
              </div>
            </div>

            <label className="settings-checkbox settings-checkbox-row">
              <input type="checkbox" checked={s.strictTitle} onChange={(e) => patch('strictTitle', e.target.checked)} />
              <span>Warn when manually edited titles don’t match a cited source</span>
            </label>
            <GoldExamples format="article" />
          </fieldset>
        </section>

        <section
          className="settings-tab-panel"
          id="settings-panel-tweets"
          role="tabpanel"
          aria-labelledby="settings-tab-tweets"
          hidden={activeSettingsTab !== 'tweets'}
        >
          <div className="settings-panel-head">
            <div>
              <h2>Tweets</h2>
              <p>Retired legacy controls.</p>
            </div>
          </div>
          <p className="editorial-alert">Tweet profiles are managed in <Link to="/dashboard">Publications</Link>.</p>
          <fieldset className="settings-fieldset settings-form" hidden aria-hidden="true">
            <div className="field">
              <label className="label" htmlFor="tweet-style">Tweet style guide</label>
              <span className="hint">Used only to select a short, verbatim tweet candidate.</span>
              <textarea id="tweet-style" rows={6} value={s.tweetStyleGuide} onChange={(e) => patch('tweetStyleGuide', e.target.value)} />
            </div>
            <div className="field settings-control-small">
              <label className="label" htmlFor="slots">Tweets per day</label>
              <input id="slots" type="number" min={0} max={24} value={s.tweetSlots} onChange={(e) => {
                const tweetSlots = Number(e.target.value);
                setS((prev) => {
                  if (!prev) return prev;
                  const editor = { ...(prev.agentSettings.editor ?? {}) };
                  delete editor.tweetSlotHours;
                  return { ...prev, tweetSlots, agentSettings: { ...prev.agentSettings, editor } };
                });
                setStatus(null);
              }} />
              <span className="hint settings-schedule-caption">{slotCaption}</span>
            </div>
            <GoldExamples format="tweet" />
          </fieldset>
        </section>

        <section
          className="settings-tab-panel settings-models-panel"
          id="settings-panel-models"
          role="tabpanel"
          aria-labelledby="settings-tab-models"
          hidden={activeSettingsTab !== 'models'}
        >
          <div className="settings-panel-head">
            <div>
              <h2>Models</h2>
              <p>{MODEL_FIELD_COUNT} assignments grouped by the work they perform.</p>
            </div>
          </div>
          <div className="model-groups">
            {ACTIVE_MODEL_GROUPS.map((group) => (
              <section className="model-group" key={group.label}>
                <div className="model-group-head">
                  <h3>{group.label}</h3>
                  <p>{group.hint}</p>
                </div>
                <div className="model-fields">{group.fields.map((field) => renderModelField(field))}</div>
              </section>
            ))}
          </div>
          <p className="hint model-catalog-status">
            {modelCatalogError
              ? modelCatalogError
              : modelCatalog
              ? modelCatalog.stale
                ? 'Showing verified fallback or stale catalog prices while OpenRouter refreshes.'
                : `Live OpenRouter catalog refreshed ${new Date(modelCatalog.fetchedAt).toLocaleString()}.`
              : 'Loading live OpenRouter prices…'}
          </p>
        </section>

        <section
          className="settings-tab-panel"
          id="settings-panel-costs"
          role="tabpanel"
          aria-labelledby="settings-tab-costs"
          hidden={activeSettingsTab !== 'costs'}
        >
          <div className="settings-panel-head">
            <div>
              <h2>Costs</h2>
              <p>Recorded provider spend and local X estimates.</p>
            </div>
          </div>
          <div className="cost-tracker">
            {costsError ? <p className="fail" role="status">{costsError}</p> : null}
            {xCostsError ? <p className="fail" role="status">{xCostsError}</p> : null}
            <Tabs
              ariaLabel="Cost provider"
              idPrefix="cost"
              options={COST_TAB_OPTIONS}
              value={activeCostTab}
              onChange={setActiveCostTab}
              className="cost-tabs"
            />

            <div
              className="cost-provider-panel"
              id="cost-panel-openrouter"
              role="tabpanel"
              aria-labelledby="cost-tab-openrouter"
              hidden={activeCostTab !== 'openrouter'}
            >
              <div className="cost-provider-total">
                <span className="label">Confirmed charges · all time</span>
                <strong>{costs ? formatCostUsd(costs.totalUsd) : '—'}</strong>
              </div>
              <div className="cost-provider-total">
                <span className="label">Confirmed charges · past 7 days</span>
                <strong>{costs ? formatCostUsd(costs.last7DaysUsd) : '—'}</strong>
              </div>
              {costs ? (
                <table className="cost-table">
                  <thead><tr><th>Category</th><th>All time</th><th>Past 7 days</th></tr></thead>
                  <tbody>{costs.breakdown.map((item) => (
                    <tr key={item.category}>
                      <th scope="row">{item.label}</th>
                      <td>{formatCostUsd(item.costUsd)}</td>
                      <td>{formatCostUsd(item.last7DaysUsd)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              ) : (
                <p className="hint">Loading recorded spend…</p>
              )}
              <p className="hint">Provider-reported OpenRouter charges, including paid requests in failed runs. This is the recorded ledger, not an account balance or invoice. Older selection requests shared the Curator category; current trend research belongs to Trends.</p>
              {costs?.estimatedUsd ? (
                <Disclosure
                  title="Older local estimates"
                  meta={`${formatCostUsd(costs.estimatedUsd)} all time · ${formatCostUsd(costs.estimatedLast7DaysUsd)} past 7 days`}
                  className="cost-estimates-disclosure"
                >
                  <p className="hint">Excluded from confirmed charges above. These older agent sessions used local model and cache prices that may differ from the amount billed.</p>
                  <table className="cost-table">
                    <thead><tr><th>Category</th><th>All time estimate</th><th>7-day estimate</th></tr></thead>
                    <tbody>{costs.breakdown.filter((item) => item.estimatedUsd > 0).map((item) => (
                      <tr key={item.category}><th scope="row">{item.label}</th><td>{formatCostUsd(item.estimatedUsd)}</td><td>{formatCostUsd(item.estimatedLast7DaysUsd)}</td></tr>
                    ))}</tbody>
                  </table>
                </Disclosure>
              ) : null}
              {costs?.unpricedRequests ? (
                <p className="hint">
                  {costs.unpricedRequests} request{costs.unpricedRequests === 1 ? '' : 's'} without provider cost data yet; not included above.
                </p>
              ) : null}
            </div>

            <div
              className="cost-provider-panel"
              id="cost-panel-x"
              role="tabpanel"
              aria-labelledby="cost-tab-x"
              hidden={activeCostTab !== 'x'}
            >
              <div className="cost-provider-total">
                <span className="label">Estimated total · all time</span>
                <strong>{xCosts ? formatCostUsd(xCosts.totalUsd) : '—'}</strong>
              </div>
              <div className="cost-provider-total">
                <span className="label">Past 7 days</span>
                <strong>{xCosts ? formatCostUsd(xCosts.last7DaysUsd) : '—'}</strong>
              </div>
              <p className="hint">The X category breakdown below covers all time.</p>
              {xCosts ? (
                xCosts.breakdown.length ? (
                  <div className="cost-breakdown">
                    {xCosts.breakdown.map((item) => (
                      <div className="cost-row" key={item.operation}>
                        <span>{item.label} · {item.units}</span>
                        <div className="cost-bar" aria-label={`${item.label}: ${formatCostUsd(item.costUsd)}`}>
                          <span style={{ width: `${Math.max((item.costUsd / largestXCost) * 100, item.costUsd ? 2 : 0)}%` }} />
                        </div>
                        <output>{formatCostUsd(item.costUsd)}</output>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="hint">No successful billable X interactions have been recorded yet.</p>
                )
              ) : (
                <p className="hint">Loading recorded estimates…</p>
              )}
              {xCosts?.unpricedInteractions ? (
                <p className="hint">
                  {xCosts.unpricedInteractions} successful media-upload interaction{xCosts.unpricedInteractions === 1 ? '' : 's'} {xCosts.unpricedInteractions === 1 ? 'is' : 'are'} unpriced and excluded from the estimate.
                </p>
              ) : null}
              <p className="hint">
                Local estimate since tracking was enabled. Read resources are deduplicated by UTC day. Article actions use Content Manage pricing; media uploads have no public unit mapping. The X Developer Console remains billing truth.
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
